import React from 'react';
import { Input } from 'reactstrap';
import {
    TermForm,
    AssumptionOfMajorContracts,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { TermsFormSelect } from './TermsFormSelect';
import InfoTooltip from '../../../../../../components/info-tooltip/info-tooltip';
import './TermsFormAssumptionOfMajorContractsBlock.scss';

const YES_NO_OPTIONS = [{ value: 'Yes' }, { value: 'No' }] as const;

const TOOLTIP_TEXT =
    'Will you assume the following that are included in the practice performance numbers provided to you?';

function booleanToYesNo(value: boolean | null | undefined): string {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '';
}

function yesNoToBoolean(value: string | null): boolean | null {
    if (value === 'Yes') return true;
    if (value === 'No') return false;
    return null;
}

const ITEMS: {
    key: keyof AssumptionOfMajorContracts;
    explanationKey: keyof AssumptionOfMajorContracts;
    label: string;
    errorKey: string;
}[] = [
    {
        key: 'labContract',
        explanationKey: 'labContractExplanation',
        label: 'Lab Contract(s)',
        errorKey: 'labContract',
    },
    {
        key: 'operatingLeases',
        explanationKey: 'operatingLeasesExplanation',
        label: 'Operating Leases',
        errorKey: 'operatingLeases',
    },
    {
        key: 'capitalLeases',
        explanationKey: 'capitalLeasesExplanation',
        label: 'Capital Leases',
        errorKey: 'capitalLeases',
    },
    {
        key: 'realEstateLease',
        explanationKey: 'realEstateLeaseExplanation',
        label: 'Real-Estate Lease',
        errorKey: 'realEstateLease',
    },
];

export interface TermsFormAssumptionOfMajorContractsBlockProps {
    termForm: TermForm;
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    rowComments?: (string | null)[];
    onRowCommentChange?: (rowIndex: number, comment: string | null) => void;
}

export const TermsFormAssumptionOfMajorContractsBlock: React.FC<
    TermsFormAssumptionOfMajorContractsBlockProps
> = ({
    termForm,
    readOnly = false,
    onChange,
    errors,
    isBuyerReview,
    isBuyerReviewConfirmed,
    rowComments,
    onRowCommentChange,
}) => {
    const amc = termForm.assumptionOfMajorContracts ?? {};

    const updateField = (field: keyof AssumptionOfMajorContracts, value: boolean | string | null) => {
        onChange?.({
            ...termForm,
            assumptionOfMajorContracts: {
                ...amc,
                [field]: value,
            },
        });
    };

    const showCommentColumn = isBuyerReview || !!isBuyerReviewConfirmed;

    return (
        <div className="terms-form-detail-block terms-form-section-underlined terms-form-assumption-of-major-contracts-block">
            <div className="terms-form-block-name terms-form-assumption-header">
                Assumption of Major Contracts
                <InfoTooltip text={TOOLTIP_TEXT} idText="assumption-major-contracts" />
            </div>
            <div className="terms-form-form-content">
                <div className="terms-form-assumption-row">
                    {ITEMS.map((item) => (
                        <div key={item.key} className="terms-form-assumption-field">
                            <span className="terms-form-assumption-label">{item.label}</span>
                            <div className="terms-form-assumption-value">
                                <TermsFormSelect
                                    value={booleanToYesNo(amc[item.key] as boolean | null | undefined)}
                                    options={YES_NO_OPTIONS}
                                    disabled={readOnly}
                                    onChange={(value) => updateField(item.key, yesNoToBoolean(value))}
                                    aria-label={item.label}
                                    invalid={errors?.has(item.errorKey)}
                                />
                            </div>
                            <div className="terms-form-assumption-explanation">
                                <span className="terms-form-assumption-label">Explanation</span>
                                <Input
                                    type="text"
                                    value={(amc[item.explanationKey] as string | null | undefined) ?? ''}
                                    disabled={readOnly}
                                    onChange={(e) => updateField(item.explanationKey, e.target.value || null)}
                                    className="form-control loi-form-input"
                                    aria-label={`${item.label} - Explanation`}
                                />
                            </div>
                        </div>
                    ))}
                    {showCommentColumn && (
                        <div className="terms-form-assumption-field">
                            <span className="terms-form-assumption-label">
                                Comments
                            </span>
                            <div className="terms-form-col-comment">
                                {isBuyerReview ? (
                                    <Input
                                        type="textarea"
                                        value={rowComments?.[0] ?? ''}
                                        onChange={(e) =>
                                            onRowCommentChange?.(
                                                0,
                                                e.target.value === ''
                                                    ? null
                                                    : e.target.value
                                            )
                                        }
                                        className="terms-form-comment-input"
                                        aria-label="Comment for Assumption of Major Contracts"
                                    />
                                ) : rowComments?.[0] ? (
                                    <Input
                                        type="textarea"
                                        value={rowComments[0] ?? ''}
                                        disabled
                                        className="terms-form-comment-input"
                                    />
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
